import { useState } from 'react';
import { Scale, Star, Shield, DollarSign, ExternalLink, X, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

interface AITool {
  name: string;
  logo: string;
  url: string;
  category: string;
  score: number;
  benefits: string[];
  pricing: string;
  security: string;
  description?: string;
}

interface AIToolRecommendationsProps {
  recommendedTools: {
    painPoint: string;
    tools: AITool[];
  }[];
  selectedPainPoints: string[];
}

export function AIToolRecommendations({ recommendedTools, selectedPainPoints }: AIToolRecommendationsProps) {
  const [selectedTool, setSelectedTool] = useState<AITool | null>(null);

  const getToolsForPainPoint = (painPoint: string) => {
    switch (painPoint) {
      case 'Too much time spent on repetitive tasks':
        return [
          {
            name: 'AutomateAI',
            logo: 'https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=128&h=128&q=80&fit=crop',
            url: 'https://automateai.dev',
            category: 'Process Automation',
            score: 4.8,
            benefits: ['90% reduction in manual tasks', 'Visual workflow builder', 'Pre-built automation templates'],
            pricing: 'From $39/user/month',
            security: 'SOC 2 Type II, GDPR',
            description: 'Enterprise-grade automation platform that eliminates repetitive tasks through AI-powered workflows.'
          },
          {
            name: 'TaskMaster Pro',
            logo: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=128&h=128&q=80&fit=crop',
            url: 'https://taskmaster.pro',
            category: 'Workflow Automation',
            score: 4.6,
            benefits: ['Smart task delegation', 'Process mining', 'Intelligent task routing'],
            pricing: 'From $25/user/month',
            security: 'ISO 27001',
            description: 'AI-driven task automation platform that learns and optimizes your workflows.'
          }
        ];

      case 'Difficulty managing workload and meeting deadlines':
        return [
          {
            name: 'DeadlineIQ',
            logo: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=128&h=128&q=80&fit=crop',
            url: 'https://deadlineiq.ai',
            category: 'Workload Management',
            score: 4.7,
            benefits: ['AI-powered deadline prediction', 'Smart workload balancing', 'Team capacity planning'],
            pricing: 'From $35/user/month',
            security: 'SOC 2 Type II',
            description: 'Intelligent workload management platform that ensures optimal task distribution and deadline compliance.'
          },
          {
            name: 'TimeOptimize',
            logo: 'https://images.unsplash.com/photo-1434626881859-194d67b2b86f?w=128&h=128&q=80&fit=crop',
            url: 'https://timeoptimize.io',
            category: 'Time Management',
            score: 4.5,
            benefits: ['Automated time tracking', 'Priority management', 'Resource optimization'],
            pricing: 'From $29/user/month',
            security: 'ISO 27001, HIPAA',
            description: 'AI-powered time management solution that helps teams meet deadlines consistently.'
          }
        ];

      case 'Lack of clarity on AI integration with existing workflows':
        return [
          {
            name: 'IntegrateAI',
            logo: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=128&h=128&q=80&fit=crop',
            url: 'https://integrateai.com',
            category: 'AI Integration Platform',
            score: 4.7,
            benefits: ['Seamless workflow integration', 'Visual integration builder', 'Legacy system support'],
            pricing: 'From $45/user/month',
            security: 'SOC 2 Type II',
            description: 'Enterprise AI integration platform that seamlessly connects with your existing tools.'
          },
          {
            name: 'WorkflowIQ',
            logo: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=128&h=128&q=80&fit=crop',
            url: 'https://workflowiq.ai',
            category: 'Process Intelligence',
            score: 4.5,
            benefits: ['Process analysis', 'Integration recommendations', 'Automated documentation'],
            pricing: 'From $39/user/month',
            security: 'ISO 27001',
            description: 'AI-powered process intelligence platform that optimizes your workflow integrations.'
          }
        ];

      case 'Fear of AI replacing jobs':
        return [
          {
            name: 'SkillBridge AI',
            logo: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=128&h=128&q=80&fit=crop',
            url: 'https://skillbridge.ai',
            category: 'AI Training & Upskilling',
            score: 4.9,
            benefits: ['Personalized learning paths', 'AI collaboration training', 'Skills gap analysis'],
            pricing: 'From $19/user/month',
            security: 'GDPR, ISO 27001',
            description: 'AI upskilling platform that helps employees adapt and grow alongside AI technology.'
          },
          {
            name: 'AIPartner',
            logo: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=128&h=128&q=80&fit=crop',
            url: 'https://aipartner.tech',
            category: 'AI Collaboration',
            score: 4.6,
            benefits: ['Human-AI teamwork training', 'Role enhancement tools', 'Performance analytics'],
            pricing: 'From $32/user/month',
            security: 'SOC 2 Type II',
            description: 'Platform focused on building effective human-AI collaboration in the workplace.'
          }
        ];

      case 'Meetings take too much time without clear outcomes':
        return [
          {
            name: 'MeetingMind',
            logo: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=128&h=128&q=80&fit=crop',
            url: 'https://meetingmind.ai',
            category: 'Meeting Intelligence',
            score: 4.8,
            benefits: ['Real-time meeting summaries', 'Action item extraction', 'Meeting effectiveness scoring'],
            pricing: 'From $22/user/month',
            security: 'SOC 2 Type II, GDPR',
            description: 'AI-powered meeting assistant that ensures productive and actionable meetings.'
          },
          {
            name: 'SmartAgenda',
            logo: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=128&h=128&q=80&fit=crop',
            url: 'https://smartagenda.io',
            category: 'Meeting Optimization',
            score: 4.7,
            benefits: ['Automated agenda creation', 'Meeting time optimization', 'Follow-up automation'],
            pricing: 'From $18/user/month',
            security: 'ISO 27001',
            description: 'Meeting management platform that optimizes meeting time and outcomes using AI.'
          }
        ];

      default:
        return [
          {
            name: 'AI Assistant Pro',
            logo: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=128&h=128&q=80&fit=crop',
            url: 'https://aiassistant.pro',
            category: 'Productivity',
            score: 4.5,
            benefits: ['Reduces manual data entry by 75%', 'Natural language processing', 'Smart automation'],
            pricing: 'From $29/user/month',
            security: 'SOC 2 Type II, GDPR',
            description: 'AI-powered productivity suite that streamlines your daily tasks.'
          },
          {
            name: 'TeamAI Hub',
            logo: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=128&h=128&q=80&fit=crop',
            url: 'https://teamaihub.com',
            category: 'Collaboration',
            score: 4.2,
            benefits: ['Real-time translation', 'Automated meeting summaries', 'Team analytics'],
            pricing: 'From $49/team/month',
            security: 'ISO 27001, HIPAA',
            description: 'Collaborative AI platform that enhances team productivity and communication.'
          }
        ];
    }
  };

  return (
    <div className="p-6 border-t border-gray-200 dark:border-gray-700">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {recommendedTools.map((recommendation) => 
          getToolsForPainPoint(recommendation.painPoint).map((tool, toolIndex) => (
            <div
              key={toolIndex}
              className="bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="p-4">
                <div className="flex items-start space-x-4">
                  <img
                    src={tool.logo}
                    alt={tool.name}
                    className="h-16 w-16 rounded-lg object-cover bg-white dark:bg-gray-800 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-base font-medium text-gray-900 dark:text-white truncate">
                          {tool.name}
                        </h4>
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-400" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {tool.score}
                          </span>
                        </div>
                      </div>
                      <a
                        href={tool.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <LinkIcon className="h-4 w-4" />
                      </a>
                    </div>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {tool.category}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                      {tool.description}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center text-gray-600 dark:text-gray-300">
                      <Shield className="h-4 w-4 mr-1 text-green-500" />
                      {tool.security.split(',')[0]}
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-gray-300">
                      <DollarSign className="h-4 w-4 mr-1 text-blue-500" />
                      {tool.pricing.split('/')[0].replace('From ', '')}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedTool(tool)}
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Tool Details Modal */}
      {selectedTool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <img
                  src={selectedTool.logo}
                  alt={selectedTool.name}
                  className="h-20 w-20 rounded-lg object-cover"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedTool.name}
                    </h2>
                    <div className="flex items-center space-x-1">
                      <Star className="h-5 w-5 text-yellow-400" />
                      <span className="text-lg font-medium text-gray-700 dark:text-gray-300">
                        {selectedTool.score}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedTool.category}
                  </p>
                  <a
                    href={selectedTool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{selectedTool.url.replace('https://', '')}</span>
                  </a>
                </div>
              </div>
              <button
                onClick={() => setSelectedTool(null)}
                className="text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Key Benefits
                </h3>
                <ul className="space-y-2">
                  {selectedTool.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pricing
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <DollarSign className="h-4 w-4 mr-1 text-blue-500" />
                    {selectedTool.pricing}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Security & Compliance
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Shield className="h-4 w-4 mr-1 text-green-500" />
                    {selectedTool.security}
                  </div>
                </div>

                {selectedTool.description && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedTool.description}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={() => setSelectedTool(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}